import React, { useEffect, useState } from "react";
import { ResponsiveRadialBar } from "@nivo/radial-bar";

const BillingReadinessChart = ({ patientData }) => {
  const [chartData, setChartData] = useState([]);
  const [readinessPercentage, setReadinessPercentage] = useState(0);
  const bgDanger = "#ff6384"; // Red color for weekly average
  const bgWarning = "#FFCE56";
  const bgSuccess = "#5796c3";

  //Colors for circle and percentages shown in Table.js

  useEffect(() => {
    // Always use patient data if it exists (don't default to dummy data)

    if (patientData && typeof patientData.billing === "number") {
      // Calculate the readiness percentage - convert string to number with parseInt
      const percentage = Math.min(100, Math.round((patientData.billing) * 100 / 16));
      setReadinessPercentage(percentage);
      // Prepare data for the radial bar chart

      const data = [
        {
          id: "Billing Readiness",
          data: [
            {
              x: "Readiness",
              y: percentage,
            },
          ],
        },
      ];

      setChartData(data);
    } else {
      // Only use dummy data for preview/development if no patient at all
      // console.log("No valid patient data found, using dummy data");
      const data = [
        {
          id: "Billing Readiness",
          data: [
            {
              x: "Readiness",
              y: 0, // Example percentage
            },
          ],
        },
      ];
      setReadinessPercentage(0);
      setChartData(data);
    }
  }, [patientData]);

  function getProgressBarColor(value) {
    //Calculate percentage based on the same formula
    const percentage = Math.min(100, Math.round((value * 100) / 30));

    if (percentage < 15) {
      return bgDanger; // Red color for values less than 4
    } else if (percentage < 30) {
      return bgWarning; // Yellow color for values between 4 and 8
    } else {
      return bgSuccess; // Green color for values greater than or equal to 8
    }
  }

  const theme = {
    background: "transparent",
    textColor: "#333333",
    fontSize: 12,
    axis: {
      domain: { line: { stroke: "#dddddd", strokeWidth: 1 } },
      ticks: {
        line: { stroke: "#dddddd", strokeWidth: 1 },
        text: { fill: "#666666", fontSize: 11 },
      },
      legend: { text: { fill: "#666666", fontSize: 13 } },
    },
    grid: { line: { stroke: "#eeeeee", strokeWidth: 1 } },
    tooltip: {
      container: {
        background: "#ffffff",
        fontSize: 12,
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
        padding: "8px 10px",
      },
    },
  };

  // Custom tooltip component
  const CustomTooltip = ({ bar }) => {
    return (
      <div
        style={{
          padding: "10px 12px",
          background: "#ffffff",
          border: "1px solid #ccc",
          borderRadius: "3px",
          color: "#333",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
          Billing Readiness
        </div>
        <div>{bar.data.y}% complete</div>
      </div>
    );
  };

  if (!chartData.length) {
    return (
      <div className="graph">
        <h3>Billing Readiness</h3>
        <div style={{ padding: "20px 0", color: "#666", textAlign: "center" }}>
          No billing data available.
        </div>
      </div>
    );
  }

  return (
    <div className="graph">
      <h3>Billing Readiness</h3>
      <div
        style={{
          height: "360px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div style={{ height: "350px", width: "350px" }}>
          <ResponsiveRadialBar
            data={chartData}
            maxValue={100}
            startAngle={0}
            endAngle={360}
            theme={theme}
            valueFormat=">-.2f"
            padding={0.4}
            cornerRadius={8}
            margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
            radialAxisStart={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
            circularAxisOuter={{
              tickSize: 5,
              tickPadding: 12,
              tickRotation: 0,
            }}
            colors={
              patientData
                ? [getProgressBarColor(patientData.billing || 0)]
                : ["#FFCE56"]
            } // Use actual patient data or default
            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
            enableRadialGrid={false}
            enableCircularGrid={false}
            tooltip={CustomTooltip}
            // Add central label with percentage
            layers={[
              "tracks",
              "bars",
              "labels",
              "legends",
              ({ center }) => {
                return (
                  <g>
                    <text
                      x={center[0]}
                      y={center[1]}
                      textAnchor="middle"
                      dominantBaseline="central"
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        fill: "#333",
                      }}
                    >
                      {readinessPercentage}%
                    </text>
                  </g>
                );
              },
            ]}
          />
        </div>
        {patientData && typeof patientData.billing === "number" ? (
          <div style={{ textAlign: "center", marginTop: "10px", fontSize: "20px"}}>
            <p>Billing Days Completed: {Math.min(16, patientData.billing)} of 16</p>
          </div>
        ) : (
          <div
            style={{ textAlign: "center", marginTop: "10px", color: "#666" }}
          >
            <p>No patient data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingReadinessChart;
